/**
 * AIAgentChatContext - Chat System Context Layer
 * 
 * ⚠️ CRITICAL: This provides the React Context interface to ChatManager
 * 
 * ARCHITECTURE RULES:
 * ✅ ALWAYS call hooks at top level of component
 * ✅ ALWAYS use ChatManager for business logic
 * ✅ NEVER call hooks conditionally or in async functions
 * ❌ NEVER create parallel state systems
 * ❌ NEVER bypass ChatManager for chat operations
 * 
 * HOOK RULES (CRITICAL):
 * - All hooks must be called at the top level
 * - Never call hooks inside conditions, loops, or async functions
 * - Never dynamically import and call hooks
 * 
 * See: docs/CHAT_SYSTEM_ARCHITECTURE.md for complete guidelines
 * See: docs/CHAT_SYSTEM_DEVELOPMENT_PATTERNS.md for extension patterns
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { CopilotFunction, CopilotMessage } from '@/hooks/ai-agents/types/database-types';
import { useChatManager } from '@/hooks/ai-agents/useChatManager';
import { useMessageSending } from '@/hooks/ai-agents/hooks/useMessageSending';
import { AIAgentDataProvider, useAIAgentData, useAssistantsSlice, useChatSlice, useAIAgentUserSlice } from './AIAgentDataContext';
import { getCopilotSidebarState, saveCopilotSidebarState } from '@/utils/localStorage';

// Create context for the chat functionality
const AIAgentChatContext = createContext<any>(null);

// Enhanced provider that wraps AIAgentDataProvider
export const AIAgentChatProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <AIAgentDataProvider>
      <AIAgentChatInternalProvider>
        {children}
      </AIAgentChatInternalProvider>
    </AIAgentDataProvider>
  );
};

// Internal provider component that uses the slices
const AIAgentChatInternalProvider = ({ children }: { children: React.ReactNode }) => {
  // Use optimized slices - only re-renders when specific slices change
  const { data: assistants, active: currentAssistant, activeId: activeAssistantId, setActiveId: setActiveAssistantId, isLoading: isLoadingAssistants, error: assistantsError } = useAssistantsSlice();
  const { isReady: assistantsInitialized, threadCount, functionCount } = useChatSlice();
  const { id: userId, isAuthenticated, preferences: userPreferences, profile: profileData } = useAIAgentUserSlice();
  
  // Functions are included in unified dashboard query (always 0, so use empty array)
  const activeFunctions: CopilotFunction[] = [];

  // Create user object for chat operations
  const user = userId ? { id: userId } : null;
  
  // ✅ CLEAN ARCHITECTURE: Use ChatManager directly (no wrapper)
  const chatManager = useChatManager(user, currentAssistant);
  
  // ✅ PROPER HOOK USAGE: Use message sending hook at top level
  const { sendMessage: sendMessageToAPI } = useMessageSending(
    user,
    chatManager.messages,
    chatManager.setMessages,
    chatManager.setIsGenerating,
    chatManager.setError,
    chatManager.activeThreadId,
    currentAssistant
  );
  
  // ✅ FIXED: Proper message submission function with optional thread ID and cache synchronization
  const submitMessage = useCallback(async (input: string, threadId?: string) => {
    // Use provided threadId or fall back to chatManager's activeThreadId
    const effectiveThreadId = threadId || chatManager.activeThreadId;
    
    if (!input.trim() || !user || !effectiveThreadId) {
      console.log('❌ Message submission blocked:', {
        hasInput: !!input.trim(),
        hasUser: !!user,
        hasThreadId: !!effectiveThreadId,
        providedThreadId: !!threadId,
        contextThreadId: !!chatManager.activeThreadId
      });
      return;
    }

    console.log('✅ Submitting message:', {
      messageLength: input.length,
      threadId: effectiveThreadId,
      assistantName: currentAssistant?.name,
      source: threadId ? 'direct' : 'context'
    });

    // ✅ CACHE SYNC: If using direct thread ID, temporarily update chatManager to use this thread
    // This ensures the useMessageSending's setMessages writes to the correct cache
    const originalThreadId = chatManager.activeThreadId;
    const needsThreadSync = threadId && threadId !== originalThreadId;
    
    if (needsThreadSync) {
      // Temporarily sync ChatManager to the provided thread ID
      const currentState = getCopilotSidebarState();
      saveCopilotSidebarState({
        ...currentState,
        currentThreadId: threadId
      });
      
      // Emit change event to sync ChatManager
      window.dispatchEvent(new CustomEvent('localStorage-change', {
        detail: { key: 'currentThreadId', value: threadId }
      }));
      
      // Small delay to ensure ChatManager syncs
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    // Set generating state
    chatManager.setIsGenerating(true);
    
    try {
      // Let useMessageSending handle optimistic updates - it will use chatManager.setMessages
      // which now points to the correct thread thanks to our sync above
      await sendMessageToAPI(input, effectiveThreadId);
    } catch (error: any) {
      console.error('❌ Message sending failed:', error);
      chatManager.setError(error.message);
    } finally {
      chatManager.setIsGenerating(false);
    }
  }, [user, chatManager, currentAssistant, sendMessageToAPI]);

  // ✅ DIRECT OPERATIONS: Use ChatManager's clear operation
  const resetConversation = useCallback(() => {
    chatManager.clearChat();
  }, [chatManager.clearChat]);
  
  // Chat is ready when we have assistants initialized
  const isChatReady = assistantsInitialized && !!userId && assistants.length > 0;

  // Memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    // Assistant data - now comes from OPTIMIZED unified query
    assistants,
    functions: activeFunctions,
    isLoadingAssistants,
    isLoadingFunctions: false, // No longer loading functions separately
    assistantsError: assistantsError ? String(assistantsError) : null,
    activeAssistantId,
    setActiveAssistantId,
    currentAssistant,
    assistantsLoaded: assistantsInitialized,
    
    // ✅ DIRECT STATE: Use ChatManager state directly
    messages: chatManager.messages,
    setMessages: chatManager.setMessages,
    isGenerating: chatManager.isGenerating,
    
    // ✅ DIRECT OPERATIONS: Use ChatManager operations directly
    submitMessage,
    resetConversation,
    initializeChat: chatManager.loadThread,
    sendMessage: submitMessage,
    clearChat: chatManager.clearChat,
    
    // Status
    isReady: isChatReady,
    isAuthenticated: !!user,
    
    // Additional unified dashboard data
    dashboardData: {
      threadCount,
      functionCount,
      profileData,
      userPreferences
    }
  }), [
    assistants,
    activeFunctions,
    isLoadingAssistants,
    assistantsError,
    activeAssistantId,
    setActiveAssistantId,
    currentAssistant,
    assistantsInitialized,
    chatManager.messages,
    chatManager.setMessages,
    chatManager.isGenerating,
    submitMessage,
    resetConversation,
    chatManager.loadThread,
    chatManager.clearChat,
    isChatReady,
    user,
    threadCount,
    functionCount,
    profileData,
    userPreferences
  ]);

  return (
    <AIAgentChatContext.Provider value={contextValue}>
      {children}
    </AIAgentChatContext.Provider>
  );
};

// Custom hook for accessing the context
export const useAIAgentChat = () => {
  const context = useContext(AIAgentChatContext);
  
  if (context === null) {
    throw new Error('useAIAgentChat must be used within an AIAgentChatProvider');
  }
  
  return context;
}; 